import * as ModelRunActions from "./ModelRunActions.js";
import * as ModelRunQueries from "../../../graphql/queries/modelrun-queries";
import {
  graphQLClient,
  executeGraphQLRequest,
  createContext,
} from "../../../graphql/graphql-client";
import {
  CreateModelRunFromModeler,
  ASSET_TYPES,
} from "../../models/ModelRunModel";
import { randomString } from "../../helpers/StringHelpers.js";
import { uploadFileToS3 } from "../../helpers/S3Helpers.js";
import { RESOURCE_TYPES } from "../../constants/AppEnums.js";
import axios from "axios";

export const fetchModelRuns = (modelerId, size, nextToken) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    if (!modelerId) {
      return;
    }
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.fetchModelRunsBegin,
        success: ModelRunActions.fetchModelRunsSuccess,
        failure: ModelRunActions.fetchModelRunsFailure,
      },
      ModelRunQueries.GET_MODELRUNS_BY_MODELER_QUERY,
      {
        filter: { primaryId: modelerId },
        size: size,
        nextToken: nextToken,
        context: createContext(user_id, "READ", "MODELER", modelerId), // Assuming you have a similar context creation function
      }
    );
  };
};

export const createNewModelRun = (modeler) => {
  return (dispatch, getState) => {
    const modelrun = CreateModelRunFromModeler(modeler);
    const user_id = getState().user.userId;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.createNewModelRunBegin,
        success: ModelRunActions.createNewModelRunSuccess,
        failure: ModelRunActions.createNewModelRunFailure,
      },
      ModelRunQueries.CREATE_NEW_MODELRUN_MUTATION,
      {
        input: modelrun,
        context: createContext(user_id, "CREATE", "MODELER", modeler.id, "MODELRUN"), // Assuming you have a createContext function
      }
    );
  };
};

export const updateModelRunProps = (modelRun) => {
  return (dispatch, getState) => {
    const updateModelRun = {
      runName: modelRun.runName,
      runStatus: modelRun.runStatus,
    }
    const user_id = getState().user.userId;
    const modeler = getState().modeler.activeModeler;
    const modelRunId = getState().modelRun.selectedModelRun?.id;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.updateModelRunBegin,
        success: ModelRunActions.updateModelRunPropsSuccess,
        failure: ModelRunActions.updateModelRunFailure,
      },
      ModelRunQueries.UPDATE_MODEL_RUN,
      {
        modelRun: updateModelRun,
        condition: { primaryId: modeler.id, secondaryId: modelRunId },
        context: createContext(user_id, "UPDATE", "MODELER", modeler.id), // Assuming you have a createContext function
      }
    );
  };
};


export const updateModelRun = (modelRun) => {
  return (dispatch, getState) => {
    const {
      assets,
      id,
      modelerId,
      generated,
      generateRunStatus,
      isDirty,
      tasks,
      ...updateModelRun
    } = modelRun;
    const user_id = getState().user.userId;
    const modeler = getState().modeler.activeModeler;
    const modelRunId = getState().modelRun.selectedModelRun?.id;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.updateModelRunBegin,
        success: ModelRunActions.updateModelRunSuccess,
        failure: ModelRunActions.updateModelRunFailure,
      },
      ModelRunQueries.UPDATE_MODEL_RUN,
      {
        modelRun: updateModelRun,
        condition: { primaryId: modeler.id, secondaryId: modelRunId },
        context: createContext(user_id, "UPDATE", "MODELER", modeler.id), // Assuming you have a createContext function
      }
    );
  };
};

export const fetchModelRunById = (modelRunId) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    const modeler = getState().modeler.activeModeler;
    if (!modeler?.id) {
      return;
    }
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.fetchModelRunByIdBegin, // Initial dispatch
        success: ModelRunActions.fetchModelRunByIdSuccess, // Success dispatch
        failure: ModelRunActions.fetchModelRunByIdFailure, // Failure dispatch
      },
      ModelRunQueries.GET_MODELRUN_BY_ID_QUERY,
      {
        id: { primaryId: modeler.id, secondaryId: modelRunId },
        context: createContext(user_id, "READ", "MODELER", modeler.id), // Assuming you have a createContext function
      }
    );
  };
};

export const deleteModelRun = (modelRunId) => {
  return (dispatch, getState) => {
    const state = getState();
    const modelerId = state.modeler.activeModeler?.id;
    const user_id = state.user.userId;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.deleteModelRunBegin, // Initial dispatch
        success: ModelRunActions.deleteModelRunSuccess, // Success dispatch
        failure: ModelRunActions.deleteModelRunFailure, // Failure dispatch
      },
      ModelRunQueries.DELETE_MODELRUN_MUTATION,
      {
        id: { primaryId: modelerId, secondaryId: modelRunId },
        context: createContext(user_id, "DELETE", "MODELER", modelerId), // Assuming you have a createContext function
      }
    );
  };
};

export const updateAndGenerateModelRun = (modelerId, modelrunId, modelRun) => {
  return async (dispatch, getState) => {
    dispatch(ModelRunActions.generateRunBegin());
    try {
      await dispatch(updateModelRun(modelRun));
      await dispatch(generateRun(modelerId, modelrunId));
    } catch (error) {
      console.error("Failed to update and publish template:", error);
    }
  };
};

export const generateRun = (modelerId, modelRunId) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.generateRunBegin, // Initial dispatch
        success: ModelRunActions.generateRunSuccess, // Success dispatch
        failure: ModelRunActions.generateRunFailure, // Failure dispatch
      },
      ModelRunQueries.GENERATE_RUN_MUTATION,
      {
        input: {
          modelerId: modelerId,
          modelRunId: modelRunId,
        },
        context: createContext(user_id, "CREATE", "MODELER", modelerId, "MODELRUN"), // Assuming you have a similar context creation function
      }
    );
  };
};

export const createModelRunAsset = (modelerId, modelRunId, filetype, file) => {
  return (dispatch, getState) => {
    dispatch(ModelRunActions.createModelRunAssetBegin(file.name));
    const cancelTokenSource = axios.CancelToken.source();
    dispatch(
      ModelRunActions.addAssetUploadCancelToken(
        file.name,
        cancelTokenSource.cancel
      )
    ); // Store the cancel function in the state
    let filename = randomString();
    let addingAsset = null;
    const user_id = getState().user.userId;
    const modeler = getState().modeler.activeModeler;
    uploadFileToS3(
      RESOURCE_TYPES.MODELER,
      `${modelerId}/${filename}`,
      file,
      null,
      (progress) => {
        dispatch({
          type: "SET_ASSET_UPLOAD_PROGRESS_ACTION",
          filename: file.name,
          progress,
        });
      },
      (res) => {
        addingAsset = {
          modelerId: modelerId,
          modelRunId: modelRunId,
          filename: file.name,
          name: filename,
          type: ASSET_TYPES[filetype],
        };
        executeGraphQLRequest(
          dispatch,
          getState,
          {
            begin: null, // No need for a separate dispatch for begin
            success: ModelRunActions.createModelRunAssetSuccess,
            failure: null, // Failure dispatch
          },
          ModelRunQueries.CREATE_ASSET_ITEM_MUTATION,
          {
            asset: addingAsset,
            context: createContext(user_id, "CREATE", "MODELER", modeler.id, "ASSETITEM"), // Assuming you have a createContext function
          }
        );
      },
      (error) => {
        console.error("Error during upload:", error);
        dispatch(ModelRunActions.removeAssetUploadCancelToken(file.name));
        // You can dispatch other actions here to inform the user about the error if needed.
      },
      cancelTokenSource.token // Pass the cancel token source to the upload function
    );
  };
};
export const cancelUpload = () => (dispatch, getState) => {
  const cancelFunctions = getState().modelRun.cancelTokens;
  for (const filename in cancelFunctions) {
    const cancelFunction = cancelFunctions[filename];
    if (cancelFunction) {
      cancelFunction("Upload cancelled by user.");
      dispatch(ModelRunActions.removeAssetUploadCancelToken(filename)); // Remove the cancel function from the state
    }
  }
};
export const deleteModelRunAsset = (assetId) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    const modeler = getState().modeler.activeModeler;
    const modelRunId = getState().modelRun.selectedModelRun.id;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.deleteModelRunAssetBegin,
        success: ModelRunActions.deleteModelRunAssetSuccess,
        failure: ModelRunActions.deleteModelRunAssetFailure, // Failure dispatch
      },
      ModelRunQueries.DELETE_MODELRUN_ASSET_MUTATION,
      {
        input: {
          primaryId: modeler.id,
          secondaryId: modelRunId,
          tertiaryId: assetId,
        },
        context: createContext(user_id, "DELETE", "MODELER", modeler.id), // Assuming you have a createContext function
      }
    );
  };
};


export const executeModelRun = (modelerId, modelRunId, computeSize, solver) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.executeModelRunBegin,
        success: ModelRunActions.executeModelRunSuccess,
        failure: ModelRunActions.executeModelRunFailure,
      },
      ModelRunQueries.EXECUTE_MODELRUN_MUTATION,
      {
        input: {
          modelerId: modelerId,
          modelRunId: modelRunId,
          solver: solver,
          computeSize: computeSize,
        },
        context: createContext(user_id, "CREATE", "MODELER", modelerId, "MODELER"),
      }
    );
  };
}

export const terminateModelRunTask = (modelerId, modelRunId, taskId) => {

  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.terminateModelRunTaskBegin,
        success: ModelRunActions.terminateModelRunTaskSuccess,
        failure: ModelRunActions.terminateModelRunTaskFailure,
      },
      ModelRunQueries.TERMINATE_MODELRUN_TASK_MUTATION,
      {
        input: {
          modelerId: modelerId,
          modelRunId: modelRunId,
          id: taskId,
        },
        context: createContext(user_id, "UPDATE", "MODELER", modelerId, "MODELER"),
      }
    );
  }
}

export const executePostProcessing = (modelerId, modelRunId, assetItemId) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.executePostProcessingBegin,
        success: ModelRunActions.executePostProcessingSuccess,
        failure: ModelRunActions.executePostProcessingFailure,
      },
      ModelRunQueries.EXECUTE_POST_PROCESSING_MUTATION,
      {
        input: {
          userId: user_id,
          operationName: "executePostProcess",
          modelerId: modelerId,
          modelRunId: modelRunId,
          assetItemId: assetItemId,
        },
        context: createContext(user_id, "UPDATE", "MODELER", modelerId, "MODELER"),
      }
    );
  };
}

export const fetchModelRunTasks = (modelerId, modelRunId, page_size, nextToken) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.fetchModelRunTasksBegin,
        success: ModelRunActions.fetchModelRunTasksSuccess,
        failure: ModelRunActions.fetchModelRunTasksFailure,
      },
      ModelRunQueries.GET_MODELRUN_TASKS_QUERY,
      {
        filter: { primaryId: modelerId, secondaryId: modelRunId },
        size: page_size,
        nextToken: nextToken,
        context: createContext(user_id, "READ", "MODELER", modelerId),
      }
    );
  };
};

export const  fetchModelRunAssets = (modelerId, modelRunId, page_size, nextToken) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.fetchModelRunAssetsBegin,
        success: ModelRunActions.fetchModelRunAssetsSuccess,
        failure: ModelRunActions.fetchModelRunAssetsFailure,
      },
      ModelRunQueries.GET_MODELRUN_ASSETS_QUERY,
      {
        filter: { primaryId: modelerId, secondaryId: modelRunId },
        size: page_size,
        nextToken: nextToken,
        context: createContext(user_id, "READ", "MODELER", modelerId),
      }
    );
  };
}

// Add a report run and fetch its assets
export const addReportRunAndFetchAssets = (modelerId, reportRun, page_size = 100, nextToken = null) => {
  return async (dispatch, getState) => {
    // First, add the report run to Redux state
    dispatch(ModelRunActions.addReportRun(reportRun));
    
    // Then fetch its assets
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.fetchReportModelRunAssetsBegin,
        success: (res) => ModelRunActions.fetchReportModelRunAssetsSuccess({
          ...res,
          modelRunId: reportRun.id, // Include the modelRunId so we know which ModelRun these assets belong to
        }),
        failure: ModelRunActions.fetchReportModelRunAssetsFailure,
      },
      ModelRunQueries.GET_MODELRUN_ASSETS_QUERY,
      {
        filter: { primaryId: modelerId, secondaryId: reportRun.id },
        size: page_size,
        nextToken: nextToken,
        context: createContext(user_id, "READ", "MODELER", modelerId),
      }
    );
  };
};

// Legacy thunk for fetching assets for reporting - doesn't affect central selectedModelRun
export const fetchReportModelRunAssets = (modelerId, modelRunId, page_size, nextToken) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.fetchReportModelRunAssetsBegin,
        success: (res) => ModelRunActions.fetchReportModelRunAssetsSuccess({
          ...res,
          modelRunId, // Include the modelRunId so we know which ModelRun these assets belong to
        }),
        failure: ModelRunActions.fetchReportModelRunAssetsFailure,
      },
      ModelRunQueries.GET_MODELRUN_ASSETS_QUERY,
      {
        filter: { primaryId: modelerId, secondaryId: modelRunId },
        size: page_size,
        nextToken: nextToken,
        context: createContext(user_id, "READ", "MODELER", modelerId),
      }
    );
  };
}

export const fetchPostProcessOperationOverview = (modelerId, operationId) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    return executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelRunActions.getPostProcessingOperationOverviewBegin,
        success: ModelRunActions.getPostProcessingOperationOverviewSuccess,
        failure: ModelRunActions.getPostProcessingOperationOverviewFailure,
      },
      ModelRunQueries.GET_OPERATION_OVERVIEW_QUERY,
      {
        input: { 
          operationId: operationId,
          userId: user_id,
          operationName: "executePostProcess",
         },
        context: createContext(user_id, "READ", "MODELER", modelerId),
      }
    );
  };
}